import { useEffect } from "react";

  /* Stage-3 C24 — identical close behavior for the three unified popovers
     (Persons / Theme / Search): window resize and any sidebar collapse /
     drawer change dismiss them. Escape + outside-click live in the hotkey
     chain and the .OV backdrop click respectively. */
export const usePopoverAutoClose = ({
  sideFly,
  searchDlg,
  setSideFly,
  setSearchDlg,
  sideCol,
  sideMob,
}: any) => {
  useEffect(() => {
    if (!sideFly && !searchDlg) return;
    const h = () => {
      setSideFly(null);
      setSearchDlg(false);
    };
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [sideFly, searchDlg]);
  useEffect(() => {
    setSideFly(null);
    setSearchDlg(false);
  }, [sideCol, sideMob]);
};

export const useAppHotkeys = ({
  searchDlg,
  setSearchDlg,
  discardDlg,
  setDiscardDlg,
  sideFly,
  setSideFly,
  subtaskDoneAsk,
  cancelSubtaskDone,
  tkCompletePend,
  setTkCompletePend,
  releaseEntityTimer,
  notifOpen,
  setNotifOpen,
  activeModal,
  form,
  cm,
  mailDlg,
  setMailDlg,
  doodleDlg,
  setDoodleDlg,
  dailyOverview,
  setDailyOverview,
  gSearchQ,
  setGSearchQ,
  ecfg,
  section,
  openModal,
  currentUser,
  setSDlgQ,
  setSDlgMore,
}: any) => {
  useEffect(() => {
    const handler = (e: any) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (searchDlg) {
          setSearchDlg(false);
          return;
        }
        if (discardDlg) {
          setDiscardDlg(null);
          return;
        }
        /* Stage-3 C24 — the unified popovers share the Escape behavior. */
        if (sideFly) {
          setSideFly(null);
          return;
        }
        /* Round 13 (C76) — both completion prompts: Escape = Cancel, which
           REVERTS the triggering action (subtask gets unticked; the status
           pill never committed, so closing restores the prior status). */
        if (subtaskDoneAsk && !activeModal) {
          cancelSubtaskDone();
          return;
        }
        if (tkCompletePend && !activeModal) {
          /* Round 17 (C102d) — Escape = Cancel: the held timer resumes. */
          if (tkCompletePend._timerHold)
            releaseEntityTimer(tkCompletePend._timerHold, "resume");
          setTkCompletePend(null);
          return;
        }
        if (notifOpen) setNotifOpen(false);
        else if (activeModal) {
          // "Did the user actually type anything, or is this the form as the
          // app seeded it?" There is no longer a level carve-out here, and the
          // reason is the ruling rather than a simplification: the app seeds NO
          // level at create (18b §1), so a level present in the form was chosen
          // by a human and is unsaved work by definition. The version this
          // replaces named `medium` and `low`; the version between them asked
          // the ladder which codes the app prefills. Nothing prefills one now.
          const hasData = Object.entries(form).some(([k, v]) => {
            if (k.startsWith("_")) return false;
            if (v == null || v === "" || v === 0 || v === false) return false;
            return v !== "todo";
          });
          if (hasData) setDiscardDlg(() => cm);
          else cm();
        }
        /* 120 §3 — the `mtgDlg` arm that stood here is gone with the Schedule
           Meeting dialog. Escape on a meeting now takes the `activeModal`
           branch ABOVE, which is the ag modal's — so a half-filled meeting asks
           before it is discarded, where the dialog dropped it silently. */
        else if (mailDlg) setMailDlg(null);
        else if (doodleDlg) setDoodleDlg(null);
        else if (dailyOverview) setDailyOverview(false);
        else if (gSearchQ) setGSearchQ("");
        return;
      }
      if (e.altKey && e.key === "n") {
        e.preventDefault();
        const addCfg = ecfg[section];
        if (addCfg) openModal(addCfg.am);
        else if (section === "ca") openModal("ca");
        else if (section === "ag") openModal("ag", {});
        else if (section === "costs")
          openModal("te", {
            coId: currentUser || "",
            date: new Date().toISOString().slice(0, 10),
            billed: false,
            min: 0,
            _lockCo: true,
          });
        return;
      }
      if (e.altKey && e.key === "t") {
        e.preventDefault();
        openModal("te", {
          coId: currentUser || "",
          date: new Date().toISOString().slice(0, 10),
          billed: false,
          min: 0,
          _lockCo: true,
        });
        return;
      }
      if (e.altKey && e.key === "f") {
        e.preventDefault();
        setSearchDlg(true);
        setSDlgQ("");
        setSDlgMore({});
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && activeModal) {
        e.preventDefault();
        const saveBtn = document.querySelector(".modal-footer .modal-save");
        if (saveBtn) (saveBtn as any).click();
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    activeModal,
    notifOpen,
    mailDlg,
    doodleDlg,
    dailyOverview,
    gSearchQ,
    section,
    discardDlg,
    form,
    sideFly,
    subtaskDoneAsk,
    tkCompletePend,
  ]);
};
